import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@/hooks';
import { useIsMobile } from '@/hooks';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useOnboardingStore, type UserProfile } from '../onboardingStore';
import { QuickStartPicker } from './QuickStartPicker';

type WelcomeStep = 'welcome' | 'pick-profile' | 'workflow';

/**
 * Multi-step welcome modal shown on first visit.
 * Step 1: Welcome message
 * Step 2: User profile selection
 * Step 3: Workflow preview (beginner only)
 */
export function WelcomeModal() {
  const [step, setStep] = useState<WelcomeStep>('welcome');
  const [selectedProfile, setSelectedProfile] = useState<UserProfile>(null);
  const modalRef = useFocusTrap(true);
  const isMobile = useIsMobile();

  const completeWelcome = useOnboardingStore((s) => s.completeWelcome);
  const startTour = useOnboardingStore((s) => s.startTour);

  const handleSkip = useCallback(() => {
    completeWelcome('exploring');
  }, [completeWelcome]);

  const handleProfileSelect = useCallback(
    (profile: UserProfile) => {
      setSelectedProfile(profile);
      if (profile === 'beginner') {
        setStep('workflow');
      } else {
        completeWelcome(profile);
      }
    },
    [completeWelcome]
  );

  const handleWorkflowComplete = useCallback(() => {
    completeWelcome(selectedProfile);
    // Auto-start the main tour for beginners
    if (selectedProfile === 'beginner') {
      // Small delay so the modal closes first
      setTimeout(() => {
        startTour('main');
      }, 300);
    }
  }, [completeWelcome, selectedProfile, startTour]);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleSkip();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => { document.removeEventListener('keydown', handleKeyDown); };
  }, [handleSkip]);

  const content = (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

      {/* Modal content */}
      <div
        ref={modalRef}
        className={cn(
          'relative mx-4 w-full overflow-hidden rounded-xl border bg-background shadow-2xl animate-onboarding-scale-in',
          isMobile ? 'max-w-lg' : 'max-w-2xl'
        )}
      >
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className={cn(
            'absolute right-4 top-4 z-10 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
          aria-label="Skip onboarding"
        >
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="p-8">
          {step === 'welcome' && (
            <WelcomeStep onContinue={() => { setStep('pick-profile'); }} />
          )}
          {step === 'pick-profile' && (
            <QuickStartPicker onSelect={handleProfileSelect} />
          )}
          {step === 'workflow' && (
            <WorkflowPreview onContinue={handleWorkflowComplete} />
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

function WelcomeStep({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="flex flex-col items-center text-center">
      {/* Logo */}
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
        <svg
          className="h-9 w-9 text-primary"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      </div>

      <h2 id="welcome-title" className="text-2xl font-bold">
        Welcome to Envelop
      </h2>
      <p className="mt-3 max-w-md text-muted-foreground">
        A modern, web-based EnergyPlus editor. Create, edit, and simulate building energy models
        directly in your browser.
      </p>

      <Button className="mt-8" size="lg" onClick={onContinue}>
        Get Started
      </Button>
    </div>
  );
}

const WORKFLOW_STEPS = [
  {
    label: 'Load Model',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    ),
  },
  {
    label: 'Edit',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    ),
  },
  {
    label: 'HVAC',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2" />
        <path d="M9.6 4.6A2 2 0 1 1 11 8H2" />
        <path d="M12.6 19.4A2 2 0 1 0 14 16H2" />
      </svg>
    ),
  },
  {
    label: 'Weather',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v2" />
        <path d="m4.93 4.93 1.41 1.41" />
        <path d="M20 12h2" />
        <path d="m19.07 4.93-1.41 1.41" />
        <path d="M15.947 12.65a4 4 0 0 0-5.925-4.128" />
        <path d="M13 22H7a5 5 0 1 1 4.9-6H13a3 3 0 0 1 0 6Z" />
      </svg>
    ),
  },
  {
    label: 'Simulate',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    ),
  },
];

function WorkflowPreview({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="flex flex-col items-center text-center">
      <h3 className="text-lg font-semibold">Your Workflow</h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Here is the typical flow from start to simulation results.
      </p>

      {/* Workflow steps */}
      <div className="mt-8 flex w-full items-center justify-center gap-2">
        {WORKFLOW_STEPS.map((ws, i) => (
          <div key={ws.label} className="flex items-center gap-2">
            <div className="flex flex-col items-center gap-1.5">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                {ws.icon}
              </div>
              <span className="text-xs font-medium">{ws.label}</span>
            </div>
            {i < WORKFLOW_STEPS.length - 1 && (
              <svg
                className="h-4 w-4 shrink-0 text-muted-foreground/40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            )}
          </div>
        ))}
      </div>

      <Button className="mt-8" size="lg" onClick={onContinue}>
        Start Tour
      </Button>
    </div>
  );
}
